import { track } from '@eventbrite/datalayer-library';
import { initializeSdk, logEvent, updateUser } from '@eventbrite/statsig';
import { Dispatch } from 'redux';
import { STATSIG_EVENT_NAMES } from '../constants/statsig';
import { getTotalAmount } from '../selectors/order';
import { getSelectedPaymentMethod } from '../selectors/payment';
import {
    getStatsigConfigFromState,
    isStatsigFreeTrialEnabledForInterstitialLogin,
} from '../selectors/statsig';
import { getNumberOfTicketsSelected } from '../selectors/tickets';
import {
    getLoginFromCheckoutPage,
    getUserId,
    isUserLoggedIn,
} from '../selectors/user';
import { CheckoutState } from '../state';
import { Experiment } from '../state/statsig';
import { trackOrderSuccess } from './eventAnalytics';

export const initializeStatsigSdk = async (state: CheckoutState) => {
    await initializeSdk({
        user: getStatsigConfigFromState(state),
    });
};
export const updateStatsigSdk = async (state: CheckoutState) => {
    await updateUser({
        user: getStatsigConfigFromState(state),
    });
};

export const updateExperimentUser =
    () => async (_: Dispatch<CheckoutState>, getState: () => CheckoutState) => {
        await updateUser({ user: { userID: getUserId(getState()) } });
    };

export const logLoginInterstitialEvent =
    (eventName: string) =>
    (_: Dispatch<CheckoutState>, getState: () => CheckoutState) =>
        logEvent(eventName, null, {
            isInterstitialStep: `${!getLoginFromCheckoutPage(getState())}`,
        });

export const isLoginInterstitialEnabled =
    () => async (_: Dispatch<CheckoutState>, getState: () => CheckoutState) => {
        const state = getState();
        if (
            isUserLoggedIn(state) ||
            !isStatsigFreeTrialEnabledForInterstitialLogin(state)
        ) {
            return false;
        }

        // This experiment is not currently in use and we're temporarily deactivating it until the 2nd iteration arrives
        return false;
    };

export const logOrderSuccessEvents =
    () =>
    (dispatch: Dispatch<CheckoutState>, getState: () => CheckoutState) => {
        const state = getState();
        const totalCost = getTotalAmount(state);
        const numberOfTickets = getNumberOfTicketsSelected(state);
        const paymentMethodUsed = getSelectedPaymentMethod(state);

        logEvent(STATSIG_EVENT_NAMES.CHECKOUT_STEP_ORDER_SUCCESS, undefined, {
            ticket_quantity: `${numberOfTickets}`,
            total_cart_value: `${totalCost}`,
            payment_method: `${paymentMethodUsed}`,
        });

        track({ eventName: 'Place Order Success', eventData: {} });

        // TODO: to be removed as part of GA deprecation
        dispatch(trackOrderSuccess());
    };

export const updateStatsigUserMetadata = async (state: CheckoutState) => {
    await updateUser({ user: getStatsigConfigFromState(state) });
};

export enum StatsigActionType {
    saveExperimentData = 'saveExperimentData',
}

export interface SaveExperimentDataAction {
    type: StatsigActionType.saveExperimentData;
    payload: Experiment;
}

export const saveExperimentDataAction = (
    experiment: Experiment,
): SaveExperimentDataAction => ({
    type: StatsigActionType.saveExperimentData,
    payload: experiment,
});

export type StatsigAction = SaveExperimentDataAction;
